// Prospects and pipeline (plan workstream 1, Phase 2): lead to client in stages,
// with source tracking and conversion reporting. Pure (rows in, numbers out) so the
// board, the report, Today and the tests agree.

export const PIPELINE_STAGES = ['enquiry', 'first_meeting', 'fact_find', 'soa', 'implemented', 'lost'] as const;
export type PipelineStage = (typeof PIPELINE_STAGES)[number];

/** The stages a live prospect moves through, in order (implemented and lost close it). */
export const OPEN_STAGES: PipelineStage[] = ['enquiry', 'first_meeting', 'fact_find', 'soa'];

export const STAGE_LABEL: Record<PipelineStage, string> = {
  enquiry: 'Enquiry',
  first_meeting: 'First meeting',
  fact_find: 'Fact find',
  soa: 'SoA',
  implemented: 'Implemented',
  lost: 'Lost',
};

export const PROSPECT_SOURCES = [
  'referral_client', 'referral_accountant', 'referral_other', 'website', 'seminar', 'existing_family', 'other',
] as const;
export type ProspectSource = (typeof PROSPECT_SOURCES)[number];

export const SOURCE_LABEL: Record<ProspectSource, string> = {
  referral_client: 'Client referral',
  referral_accountant: 'Accountant referral',
  referral_other: 'Other referral',
  website: 'Website',
  seminar: 'Seminar or event',
  existing_family: 'Family of a client',
  other: 'Other',
};

export interface StageEvent { stage: PipelineStage; at: string }

export interface ProspectLike {
  id: string;
  firstName: string;
  surname: string;
  stage: PipelineStage;
  source: string | null;
  estimatedFumCents: number | null;
  nextStep: string | null;
  nextStepDue: string | null;
  createdAt: string | Date;
  convertedAt?: string | Date | null;
  stageHistory?: StageEvent[] | null;
}

const BRISBANE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Brisbane' });
/** Calendar day in Brisbane for a timestamp; a bare YYYY-MM-DD is taken as is. */
const isoDay = (d: string | Date) => (typeof d === 'string' && d.length === 10 ? d : BRISBANE.format(new Date(d)));
const daysBetween = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

export const isOpenStage = (s: PipelineStage) => OPEN_STAGES.includes(s);

/** Append a stage change; unchanged stage leaves the history as it was. */
export function withStageChange(history: StageEvent[] | null | undefined, from: PipelineStage | null, to: PipelineStage, at: string): StageEvent[] {
  const h = [...(history ?? [])];
  if (from === to && h.length) return h;
  h.push({ stage: to, at });
  return h;
}

export interface StageColumn {
  stage: PipelineStage;
  label: string;
  count: number;
  estimatedFumCents: number;
}

/** Board columns: one per stage, with the count and the estimated FUM in it. */
export function stageColumns(rows: ProspectLike[]): StageColumn[] {
  return PIPELINE_STAGES.map((stage) => {
    const inStage = rows.filter((r) => r.stage === stage);
    return {
      stage,
      label: STAGE_LABEL[stage],
      count: inStage.length,
      estimatedFumCents: inStage.reduce((n, r) => n + (r.estimatedFumCents ?? 0), 0),
    };
  });
}

export interface SourceConversion {
  source: string;
  label: string;
  total: number;
  open: number;
  converted: number;
  lost: number;
  /** converted / (converted + lost); null until one is closed. */
  conversionRate: number | null;
  /** Mean whole days from enquiry to implemented; null when none converted. */
  avgDaysToConvert: number | null;
  convertedFumCents: number;
}

const sourceLabel = (s: string) => (SOURCE_LABEL as Record<string, string>)[s] ?? (s === 'unknown' ? 'Not recorded' : s);

/**
 * Conversion by source, closed prospects only in the rate (an open lead has not won or
 * lost yet). Optional window: only prospects created on or after `since`.
 */
export function conversionBySource(rows: ProspectLike[], since?: string): { rows: SourceConversion[]; totals: SourceConversion } {
  const scoped = since ? rows.filter((r) => isoDay(r.createdAt) >= since) : rows;
  const groups = new Map<string, ProspectLike[]>();
  for (const r of scoped) {
    const k = r.source || 'unknown';
    groups.set(k, [...(groups.get(k) ?? []), r]);
  }
  const summarise = (source: string, list: ProspectLike[]): SourceConversion => {
    const converted = list.filter((r) => r.stage === 'implemented');
    const lost = list.filter((r) => r.stage === 'lost').length;
    const closed = converted.length + lost;
    const durations = converted
      .filter((r) => r.convertedAt)
      .map((r) => daysBetween(isoDay(r.createdAt), isoDay(r.convertedAt!)));
    return {
      source,
      label: sourceLabel(source),
      total: list.length,
      open: list.filter((r) => isOpenStage(r.stage)).length,
      converted: converted.length,
      lost,
      conversionRate: closed ? converted.length / closed : null,
      avgDaysToConvert: durations.length ? Math.round(durations.reduce((a, b) => a + b, 0) / durations.length) : null,
      convertedFumCents: converted.reduce((n, r) => n + (r.estimatedFumCents ?? 0), 0),
    };
  };
  const out = [...groups.entries()].map(([s, list]) => summarise(s, list))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
  return { rows: out, totals: summarise('all', scoped) };
}

/** Days a prospect has sat in its current stage (from the last stage event, else created). */
export function daysInStage(p: ProspectLike, today: string): number {
  const last = [...(p.stageHistory ?? [])].reverse().find((e) => e.stage === p.stage);
  return Math.max(0, daysBetween(last ? isoDay(last.at) : isoDay(p.createdAt), today));
}

export interface ProspectFollowUp {
  prospectId: string;
  name: string;
  stage: PipelineStage;
  date: string;
  detail: string;
}

/**
 * Today's follow-ups: open prospects whose next step is due today or overdue.
 * Earliest first. Closed prospects never appear.
 */
export function prospectFollowUps(rows: ProspectLike[], today: string): ProspectFollowUp[] {
  const out: ProspectFollowUp[] = [];
  for (const p of rows) {
    if (!isOpenStage(p.stage) || !p.nextStepDue) continue;
    const due = p.nextStepDue.slice(0, 10);
    if (due > today) continue;
    const late = daysBetween(due, today);
    const step = p.nextStep?.trim() ? (p.nextStep.length > 60 ? `${p.nextStep.slice(0, 57).trimEnd()}...` : p.nextStep.trim()) : 'Next step';
    out.push({
      prospectId: p.id,
      name: `${p.surname}, ${p.firstName}`,
      stage: p.stage,
      date: due,
      detail: `${STAGE_LABEL[p.stage]}: ${step} · ${late === 0 ? 'due today' : `overdue ${late} day${late === 1 ? '' : 's'}`}`,
    });
  }
  return out.sort((a, b) => a.date.localeCompare(b.date) || a.name.localeCompare(b.name));
}
